const API_BASE = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build full URL from a path relative to the Django API root.
 * REACT_APP_API_URL should be set to e.g. "https://api.yourdomain.com"
 * in production and left empty (empty string) for local CRA proxy.
 */
function url(path) {
  return `${API_BASE}${path}`;
}

/**
 * Shared fetch wrapper.
 * - Attaches Content-Type and (later) Authorization headers.
 * - Parses JSON response body.
 * - Throws a structured error for non-2xx responses.
 */
async function request(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url(path), {
    ...options,
    headers,
  });

  // Attempt to parse JSON regardless of status so error bodies are available
  let body;
  try {
    body = await response.json();
  } catch (err) {
    console.warn('Failed to parse JSON response body:', err);
    body = null;
  }

  if (!response.ok) {
    const message =
      body?.detail ||
      body?.message ||
      (typeof body === 'object' && body !== null
        ? JSON.stringify(body)
        : `HTTP ${response.status}`);
    const err = new Error(message);
    err.status = response.status;
    err.body   = body;
    throw err;
  }

  return body;
}

// ---------------------------------------------------------------------------
// M&A Analysis endpoints  (/api/ma/)
// ---------------------------------------------------------------------------

/**
 * POST /api/ma/analyze/
 *
 * Submits financial inputs, runs the valuation engine, persists the result,
 * and returns the full analysis payload.
 *
 * @param {Object} payload - AgencyValuation input fields (all strings/numbers)
 * @returns {Promise<Object>} { analysis_id, ebitda, score, multiple, valuation }
 */
export async function analyzeAgency(payload) {
  return request('/api/ma/analyze/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * GET /api/ma/analysis/
 *
 * Returns a paginated list of the authenticated user's stored analyses,
 * ordered by most recent first.
 *
 * @param {number} page - Page number (default 1)
 * @returns {Promise<Object>} { count, next, previous, results: [...] }
 */
export async function listAnalyses(page = 1) {
  return request(`/api/ma/analysis/?page=${page}`);
}

/**
 * GET /api/ma/analysis/:id/
 *
 * Retrieves a single stored analysis by primary key.
 * Returns 404 if the record does not exist or belongs to another user.
 *
 * @param {number} id - MAAnalysisResult primary key
 * @returns {Promise<Object>} Full analysis payload including input_snapshot
 */
export async function getAnalysis(id) {
  return request(`/api/ma/analysis/${id}/`);
}

/**
 * GET /api/ma/analysis/:id/pdf/
 *
 * Downloads the valuation report as a styled PDF file.
 * Monetary values are displayed in the currency selected at submission time.
 *
 * @param {number} id - MAAnalysisResult primary key
 */
export async function downloadPDF(id) {
  const response = await fetch(url(`/api/ma/analysis/${id}/pdf/`));
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `valuation-${id}.pdf`;
  a.click();
  URL.revokeObjectURL(href);
}

/**
 * GET /api/ma/analysis/:id/xlsx/
 *
 * Downloads the valuation workbook as a formatted Excel file (3 sheets).
 * Monetary values are displayed in the currency selected at submission time.
 *
 * @param {number} id - MAAnalysisResult primary key
 */
export async function downloadXLSX(id) {
  const response = await fetch(url(`/api/ma/analysis/${id}/xlsx/`));
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `valuation-${id}.xlsx`;
  a.click();
  URL.revokeObjectURL(href);
}

/**
 * POST /api/ma/contact/
 *
 * Saves a contact form submission and triggers Slack + Google Sheets
 * notifications in the background. The response is immediate.
 *
 * @param {Object} payload - { analysis_id, interest, name, email, website }
 * @returns {Promise<Object>} { status: 'ok', lead_id: number }
 */
export async function submitContact({ analysisId, interest, name, email, website }) {
  return request('/api/ma/contact/', {
    method: 'POST',
    body: JSON.stringify({
      analysis_id: analysisId || null,
      interest,
      name,
      email,
      website: website || '',
    }),
  });
}
